import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { GenericOAuth2, OAuth2AuthenticateOptions, OAuth2RefreshTokenOptions } from '@capacitor-community/generic-oauth2';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PlatformDetectionService } from './platform-detection.service';
import { JwtHelperService } from '@auth0/angular-jwt';

@Injectable({
  providedIn: 'root'
})
export class MobileOAuth2Service {
  private readonly ACCESS_TOKEN_KEY = 'mobile_access_token';
  private readonly REFRESH_TOKEN_KEY = 'mobile_refresh_token';
  private readonly ID_TOKEN_KEY = 'mobile_id_token';
  private readonly EXPIRES_AT_KEY = 'mobile_expires_at';
  private readonly AUTH_REDIRECT_URL_KEY = 'auth_redirect_url';

  // Token refresh configuration
  private readonly TOKEN_REFRESH_THRESHOLD_SECONDS = 300; // Refresh if token expires in less than 5 minutes
  private readonly MAX_REFRESH_RETRIES = 3;
  private readonly RETRY_DELAY_MS = 1000; // 1 second base delay

  private isRefreshing = false;
  private refreshPromise: Promise<void> | null = null;
  private jwtHelper = new JwtHelperService();
  private isReauthenticating = false; // Prevent multiple simultaneous re-authentication attempts

  private loginSuccessSubject = new BehaviorSubject<boolean>(false);
  public loginSuccess$: Observable<boolean> = this.loginSuccessSubject.asObservable();

  constructor(
    private platformDetection: PlatformDetectionService,
    private router: Router,
    private ngZone: NgZone
  ) {}

  /**
   * Initiates the OAuth2 login flow for mobile platforms
   */
  async login(): Promise<void> {
    if (!this.platformDetection.isNative) {
      console.warn('MobileOAuth2Service.login() called on non-native platform');
      return;
    }

    try {
      const options = this.buildOAuth2Options();

      if (environment.oauth2Config?.logsEnabled) {
        console.log('=== OAuth2 Login Debug ===');
        console.log('Platform:', this.platformDetection.getPlatform());
        console.log('Full OAuth2 Options:', JSON.stringify(options, null, 2));
        console.log('Android redirect URL:', options.android?.redirectUrl);
        console.log('iOS redirect URL:', options.ios?.redirectUrl);
        console.log('Environment redirect URL:', environment.oauth2Config.redirectUrl);
        console.log('========================');
      }

      const response = await GenericOAuth2.authenticate(options);

      if (environment.oauth2Config?.logsEnabled) {
        console.log('OAuth2 Response:', response);
      }

      // Store tokens
      this.storeTokens(response);

      // Notify subscribers and navigate
      // The app.component.ts will handle initialization via loginSuccess$ subscription
      this.ngZone.run(() => {
        this.loginSuccessSubject.next(true);

        // Check for stored redirect URL (from re-authentication flow)
        const redirectUrl = localStorage.getItem(this.AUTH_REDIRECT_URL_KEY);

        if (redirectUrl) {
          // User was re-authenticated, redirect back to where they were
          localStorage.removeItem(this.AUTH_REDIRECT_URL_KEY);
          if (environment.oauth2Config?.logsEnabled) {
            console.log('OAuth2 login successful, redirecting to stored URL:', redirectUrl);
          }
          void this.router.navigateByUrl(redirectUrl);
        } else {
          // Normal login flow, navigate to root
          if (environment.oauth2Config?.logsEnabled) {
            console.log('OAuth2 login successful, navigating to root');
          }
          void this.router.navigate(['/']);
        }
      });

    } catch (error) {
      console.error('OAuth2 Login Error:', error);
      throw error;
    }
  }

  /**
   * Logs out the user and clears stored tokens
   */
  async logout(): Promise<void> {
    this.clearTokens();

    // Clear redirect URL on explicit logout (user doesn't want to return to previous page)
    localStorage.removeItem(this.AUTH_REDIRECT_URL_KEY);

    this.ngZone.run(() => {
      this.loginSuccessSubject.next(false);
      this.router.navigate(['/login']);
    });
  }

  /**
   * Gets the access token with automatic refresh if expired or about to expire
   * Implements silent token refresh mechanism for mobile platforms
   */
  async getAccessToken(): Promise<string> {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] [MOBILE-OAUTH2] === getAccessToken() called ===`);

    const token = localStorage.getItem(this.ACCESS_TOKEN_KEY);

    if (!token) {
      const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
      console.error(`[${timestamp}] [MOBILE-OAUTH2] ❌ No access token found in localStorage`);
      console.error(`[${timestamp}] [MOBILE-OAUTH2] Refresh token exists: ${!!refreshToken}`);
      console.error(`[${timestamp}] [MOBILE-OAUTH2] Triggering re-authentication...`);

      // Trigger re-authentication flow
      await this.handleTokenRefreshFailure();

      // If we reach here, re-authentication was initiated
      throw new Error('No access token found. Re-authentication initiated.');
    }

    console.log(`[${timestamp}] [MOBILE-OAUTH2] ✅ Access token exists in localStorage`);

    try {
      // Check if token is expired or about to expire
      console.log(`[${timestamp}] [MOBILE-OAUTH2] Checking token expiration...`);
      const isExpired = this.jwtHelper.isTokenExpired(token);
      const expirationDate = this.jwtHelper.getTokenExpirationDate(token);

      if (expirationDate) {
        const now = new Date();
        const timeUntilExpiry = (expirationDate.getTime() - now.getTime()) / 1000; // in seconds

        console.log(`[${timestamp}] [MOBILE-OAUTH2] Token expiration info:`);
        console.log(`  - Expiration date: ${expirationDate.toISOString()}`);
        console.log(`  - Current time: ${now.toISOString()}`);
        console.log(`  - Time until expiry: ${timeUntilExpiry} seconds (${Math.floor(timeUntilExpiry / 60)} minutes)`);
        console.log(`  - Is expired: ${isExpired}`);
        console.log(`  - Refresh threshold: ${this.TOKEN_REFRESH_THRESHOLD_SECONDS} seconds`);

        // If token is expired or about to expire, refresh it
        if (isExpired || timeUntilExpiry < this.TOKEN_REFRESH_THRESHOLD_SECONDS) {
          console.warn(`[${timestamp}] [MOBILE-OAUTH2] ⚠️ Token is ${isExpired ? 'EXPIRED' : 'about to expire'}. Initiating silent refresh...`);

          const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
          if (!refreshToken) {
            console.error(`[${timestamp}] [MOBILE-OAUTH2] ❌ No refresh token available. Cannot refresh.`);
            console.error(`[${timestamp}] [MOBILE-OAUTH2] Triggering re-authentication...`);

            // Trigger re-authentication flow
            await this.handleTokenRefreshFailure();

            // If we reach here, re-authentication was initiated
            throw new Error('No refresh token available. Re-authentication initiated.');
          }

          console.log(`[${timestamp}] [MOBILE-OAUTH2] ✅ Refresh token exists, proceeding with refresh...`);

          // If already refreshing, wait for the existing refresh to complete
          if (this.isRefreshing && this.refreshPromise) {
            console.log(`[${timestamp}] [MOBILE-OAUTH2] Token refresh already in progress, waiting...`);
            await this.refreshPromise;
          } else {
            // Start a new refresh
            console.log(`[${timestamp}] [MOBILE-OAUTH2] Starting new token refresh...`);
            await this.refreshTokenWithRetry();
          }

          // Get the new token after refresh
          const newToken = localStorage.getItem(this.ACCESS_TOKEN_KEY);
          if (!newToken) {
            console.error(`[${timestamp}] [MOBILE-OAUTH2] ❌ Token refresh completed but no token found in localStorage`);
            throw new Error('Token refresh failed. Please login again.');
          }

          console.log(`[${timestamp}] [MOBILE-OAUTH2] ✅ Token refreshed successfully, returning new token`);
          return newToken;
        } else {
          console.log(`[${timestamp}] [MOBILE-OAUTH2] ✅ Token is valid and not expiring soon (${Math.floor(timeUntilExpiry / 60)} minutes remaining)`);
        }
      } else {
        console.warn(`[${timestamp}] [MOBILE-OAUTH2] ⚠️ Could not determine token expiration date from JWT`);
        console.warn(`[${timestamp}] [MOBILE-OAUTH2] Returning token anyway - backend will validate`);
      }
    } catch (error) {
      // If JWT parsing or refresh fails, log detailed error
      console.error(`[${timestamp}] [MOBILE-OAUTH2] ❌ Error in token validation/refresh:`, error);
      console.error(`[${timestamp}] [MOBILE-OAUTH2] Error details:`, {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : JSON.stringify(error),
        stack: error instanceof Error ? error.stack : undefined
      });

      // Re-throw the error so the caller knows something went wrong
      throw error;
    }

    console.log(`[${timestamp}] [MOBILE-OAUTH2] ✅ Returning existing valid token`);
    return token;
  }

  /**
   * Gets the ID token
   */
  getIdToken(): string | null {
    const idToken = localStorage.getItem(this.ACCESS_TOKEN_KEY);
    console.log('=== getIdToken() called ===');
    console.log('ID token exists:', !!idToken);
    if (idToken) {
      console.log('ID token (first 50 chars):', idToken.substring(0, 50) + '...');
    }
    return idToken;
  }

  /**
   * Checks if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem(this.ACCESS_TOKEN_KEY);

    console.log('=== isAuthenticated() check ===');
    console.log('Access token exists:', !!token);

    // Simply check if token exists - let the API server validate expiration
    return !!token;
  }

  /**
   * Refreshes the access token using the refresh token with retry logic
   */
  private async refreshTokenWithRetry(): Promise<void> {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] [MOBILE-OAUTH2] === refreshTokenWithRetry() called ===`);

    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing && this.refreshPromise) {
      console.log(`[${timestamp}] [MOBILE-OAUTH2] Refresh already in progress, waiting for existing refresh...`);
      return this.refreshPromise;
    }

    console.log(`[${timestamp}] [MOBILE-OAUTH2] Starting new refresh with retry logic (max ${this.MAX_REFRESH_RETRIES} attempts)`);
    this.isRefreshing = true;

    this.refreshPromise = (async () => {
      let lastError: any;

      for (let attempt = 1; attempt <= this.MAX_REFRESH_RETRIES; attempt++) {
        try {
          console.log(`[${timestamp}] [MOBILE-OAUTH2] 🔄 Token refresh attempt ${attempt}/${this.MAX_REFRESH_RETRIES}`);
          await this.refreshToken();
          console.log(`[${timestamp}] [MOBILE-OAUTH2] ✅ Token refresh successful on attempt ${attempt}`);
          return;
        } catch (error) {
          lastError = error;
          console.error(`[${timestamp}] [MOBILE-OAUTH2] ❌ Token refresh attempt ${attempt} failed`);
          console.error(`[${timestamp}] [MOBILE-OAUTH2] Error:`, error instanceof Error ? error.message : JSON.stringify(error));

          if (attempt < this.MAX_REFRESH_RETRIES) {
            // Exponential backoff: 1s, 2s, 4s
            const delay = this.RETRY_DELAY_MS * Math.pow(2, attempt - 1);
            console.log(`[${timestamp}] [MOBILE-OAUTH2] ⏳ Retrying in ${delay}ms (attempt ${attempt + 1}/${this.MAX_REFRESH_RETRIES})...`);
            await this.delay(delay);
          }
        }
      }

      // All retries failed - trigger re-authentication
      console.error(`[${timestamp}] [MOBILE-OAUTH2] ❌❌❌ All ${this.MAX_REFRESH_RETRIES} token refresh attempts failed`);
      console.error(`[${timestamp}] [MOBILE-OAUTH2] Last error:`, lastError);
      console.error(`[${timestamp}] [MOBILE-OAUTH2] Triggering automatic re-authentication...`);

      // Trigger re-authentication flow instead of just throwing error
      await this.handleTokenRefreshFailure();

      // If we reach here, re-authentication was initiated
      // Throw error to stop current request flow
      throw new Error('Token refresh failed. Re-authentication initiated.');
    })();

    try {
      await this.refreshPromise;
      console.log(`[${timestamp}] [MOBILE-OAUTH2] ✅ refreshTokenWithRetry() completed successfully`);
    } catch (error) {
      console.error(`[${timestamp}] [MOBILE-OAUTH2] ❌ refreshTokenWithRetry() failed:`, error);
      throw error;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
      console.log(`[${timestamp}] [MOBILE-OAUTH2] Refresh state reset (isRefreshing=false)`);
    }
  }

  /**
   * Refreshes the access token using the refresh token
   */
  private async refreshToken(): Promise<void> {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] [MOBILE-OAUTH2] === refreshToken() called ===`);

    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);

    if (!refreshToken) {
      console.error(`[${timestamp}] [MOBILE-OAUTH2] ❌ No refresh token found in localStorage`);
      console.error(`[${timestamp}] [MOBILE-OAUTH2] localStorage keys:`, Object.keys(localStorage));
      throw new Error('No refresh token found. Please login again.');
    }

    console.log(`[${timestamp}] [MOBILE-OAUTH2] ✅ Refresh token exists in localStorage`);
    console.log(`[${timestamp}] [MOBILE-OAUTH2] Refresh token (first 20 chars): ${refreshToken.substring(0, 20)}...`);

    try {
      // Check if OAuth2 config is available
      if (!environment.oauth2Config) {
        console.error(`[${timestamp}] [MOBILE-OAUTH2] ❌ OAuth2 config not found in environment`);
        throw new Error('OAuth2 configuration missing');
      }

      const options: OAuth2RefreshTokenOptions = {
        appId: environment.oauth2Config.appId,
        accessTokenEndpoint: environment.oauth2Config.accessTokenEndpoint,
        refreshToken: refreshToken,
        scope: environment.oauth2Config.scope,
      };

      console.log(`[${timestamp}] [MOBILE-OAUTH2] OAuth2 refresh options:`, {
        appId: options.appId,
        accessTokenEndpoint: options.accessTokenEndpoint,
        scope: options.scope,
        hasRefreshToken: !!options.refreshToken,
        refreshTokenLength: refreshToken.length
      });

      console.log(`[${timestamp}] [MOBILE-OAUTH2] 🔄 Calling GenericOAuth2.refreshToken()...`);
      const response = await GenericOAuth2.refreshToken(options);
      console.log(`[${timestamp}] [MOBILE-OAUTH2] ✅ GenericOAuth2.refreshToken() returned successfully`);

      console.log(`[${timestamp}] [MOBILE-OAUTH2] Response structure:`, {
        hasAccessToken: !!response.access_token,
        hasRefreshToken: !!response.refresh_token,
        hasIdToken: !!response.id_token,
        hasExpiresIn: !!response.expires_in,
        accessTokenLength: response.access_token?.length || 0
      });

      if (environment.oauth2Config?.logsEnabled) {
        console.log(`[${timestamp}] [MOBILE-OAUTH2] Full Token Refresh Response:`, response);
      }

      if (!response.access_token) {
        console.error(`[${timestamp}] [MOBILE-OAUTH2] ❌ No access token in refresh response`);
        throw new Error('Token refresh response missing access token');
      }

      // Update stored tokens
      console.log(`[${timestamp}] [MOBILE-OAUTH2] Storing refreshed tokens...`);
      this.storeTokens(response);

      console.log(`[${timestamp}] [MOBILE-OAUTH2] ✅✅✅ Token refresh completed successfully`);

    } catch (error) {
      console.error(`[${timestamp}] [MOBILE-OAUTH2] ❌❌❌ Token Refresh Error`);
      console.error(`[${timestamp}] [MOBILE-OAUTH2] Error type:`, error instanceof Error ? error.constructor.name : typeof error);
      console.error(`[${timestamp}] [MOBILE-OAUTH2] Error message:`, error instanceof Error ? error.message : JSON.stringify(error));
      console.error(`[${timestamp}] [MOBILE-OAUTH2] Error stack:`, error instanceof Error ? error.stack : 'N/A');

      if (error && typeof error === 'object') {
        console.error(`[${timestamp}] [MOBILE-OAUTH2] Error details:`, JSON.stringify(error, null, 2));
      }

      // Don't auto-logout here - let the caller decide
      console.error(`[${timestamp}] [MOBILE-OAUTH2] Token refresh failed. User may need to re-login.`);
      throw new Error(`Token refresh failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Handles token refresh failure by triggering automatic re-authentication
   * This method preserves the current route and redirects user to Entra ID login
   */
  private async handleTokenRefreshFailure(): Promise<void> {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] [MOBILE-OAUTH2] === handleTokenRefreshFailure() called ===`);

    // Prevent multiple simultaneous re-authentication attempts
    if (this.isReauthenticating) {
      console.log(`[${timestamp}] [MOBILE-OAUTH2] Re-authentication already in progress, skipping...`);
      return;
    }

    this.isReauthenticating = true;

    try {
      // Store current route for deep linking (redirect after successful login)
      const currentRoute = this.router.url;

      // Don't store login or callback routes
      if (currentRoute &&
          !currentRoute.includes('/login') &&
          !currentRoute.includes('/callback') &&
          !currentRoute.includes('/unauthorized') &&
          !currentRoute.includes('/access-denied')) {

        console.log(`[${timestamp}] [MOBILE-OAUTH2] Storing current route for redirect: ${currentRoute}`);
        localStorage.setItem(this.AUTH_REDIRECT_URL_KEY, currentRoute);
      } else {
        console.log(`[${timestamp}] [MOBILE-OAUTH2] Current route is login/callback, not storing for redirect`);
      }

      // Clear all tokens to ensure clean state
      console.log(`[${timestamp}] [MOBILE-OAUTH2] Clearing all tokens...`);
      this.clearTokens();

      // Notify subscribers that user is logged out
      this.ngZone.run(() => {
        this.loginSuccessSubject.next(false);
      });

      console.log(`[${timestamp}] [MOBILE-OAUTH2] ✅ Triggering re-authentication via login()...`);

      // Trigger re-authentication by calling login()
      // This will redirect user to Entra ID for authentication
      await this.login();

    } catch (error) {
      console.error(`[${timestamp}] [MOBILE-OAUTH2] ❌ Error during re-authentication:`, error);

      // Even if login fails, navigate to login screen
      this.ngZone.run(() => {
        void this.router.navigate(['/login']);
      });
    } finally {
      this.isReauthenticating = false;
    }
  }

  /**
   * Utility method to delay execution
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Builds OAuth2 options based on platform and environment
   */
  private buildOAuth2Options(): OAuth2AuthenticateOptions {
    if (!environment.oauth2Config) {
      throw new Error('OAuth2 configuration not found in environment');
    }

    const baseOptions: OAuth2AuthenticateOptions = {
      appId: environment.oauth2Config.appId,
      authorizationBaseUrl: environment.oauth2Config.authorizationBaseUrl,
      accessTokenEndpoint: environment.oauth2Config.accessTokenEndpoint,
      scope: environment.oauth2Config.scope,
      responseType: environment.oauth2Config.responseType as any,
      pkceEnabled: environment.oauth2Config.pkceEnabled,
      logsEnabled: environment.oauth2Config.logsEnabled,
    };

    // Platform-specific redirect URL
    if (this.platformDetection.isAndroid) {
      console.log('Android redirect URL:', environment.oauth2Config.redirectUrl);
      baseOptions.android = {
        redirectUrl: environment.oauth2Config.redirectUrl,
        pkceEnabled: environment.oauth2Config.pkceEnabled, // Explicitly set for Android
        responseType: environment.oauth2Config.responseType as any,
      };
    } else if (this.platformDetection.isIOS) {
      baseOptions.ios = {
        redirectUrl: environment.oauth2Config.redirectUrl,
        pkceEnabled: environment.oauth2Config.pkceEnabled, // Explicitly set for iOS
        responseType: environment.oauth2Config.responseType as any,
      };
    } else {
      console.log('Web redirect URL:', window.location.origin + '/');
      // Web platform
      baseOptions.web = {
        redirectUrl: window.location.origin + '/',
        windowOptions: 'height=600,width=400',
      };
    }

    return baseOptions;
  }

  /**
   * Stores authentication tokens in localStorage
   */
  private storeTokens(response: any): void {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] === storeTokens() called ===`);
    console.log(`[${timestamp}] Platform: ${this.platformDetection.getPlatform()}`);
    console.log(`[${timestamp}] Response structure:`, {
      hasAccessToken: !!response.access_token,
      hasRefreshToken: !!response.refresh_token,
      hasIdToken: !!response.id_token,
      hasAuthorizationResponse: !!response.authorization_response,
      hasAccessTokenResponse: !!response.access_token_response,
      hasExpiresIn: !!response.expires_in
    });

    // iOS-specific: Extract tokens from access_token_response if they're nested there
    // On iOS, the GenericOAuth2 plugin returns tokens in a nested structure
    let accessToken = response.access_token;
    let refreshToken = response.refresh_token;
    let expiresIn = response.expires_in;

    if (this.platformDetection.isIOS && !accessToken && response.access_token_response) {
      console.log(`[${timestamp}] [iOS] Extracting tokens from access_token_response nested object`);
      accessToken = response.access_token_response.access_token;
      refreshToken = response.access_token_response.refresh_token;
      expiresIn = response.access_token_response.expires_in;

      console.log(`[${timestamp}] [iOS] Extracted tokens:`, {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        hasExpiresIn: !!expiresIn
      });
    }

    // Store access token
    if (accessToken) {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
      console.log(`[${timestamp}] ✅ Access token stored`);

      // Try to extract and store expiration time
      try {
        const expirationDate = this.jwtHelper.getTokenExpirationDate(accessToken);
        if (expirationDate) {
          localStorage.setItem(this.EXPIRES_AT_KEY, expirationDate.getTime().toString());
          console.log(`[${timestamp}] ✅ Token expiration stored: ${expirationDate.toISOString()}`);
        } else if (expiresIn) {
          // Fallback: calculate expiration from expires_in
          const expiresAt = new Date(Date.now() + (expiresIn * 1000));
          localStorage.setItem(this.EXPIRES_AT_KEY, expiresAt.getTime().toString());
          console.log(`[${timestamp}] ✅ Token expiration calculated from expires_in: ${expiresAt.toISOString()}`);
        }
      } catch (error) {
        console.warn(`[${timestamp}] ⚠️ Could not extract token expiration:`, error);
      }
    } else {
      console.error(`[${timestamp}] ❌ No access token found in response!`);
    }

    // Store refresh token
    if (refreshToken) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
      console.log(`[${timestamp}] ✅ Refresh token stored`);
    } else {
      console.warn(`[${timestamp}] ⚠️ No refresh token in response - token refresh may not work`);
    }

    // ID token can be in multiple locations - check all possibilities
    let idToken = response.id_token;

    // Check authorization_response for id_token (common in OAuth2 flows)
    if (!idToken && response.authorization_response?.id_token) {
      idToken = response.authorization_response.id_token;
      console.log(`[${timestamp}] Found id_token in authorization_response`);
    }

    // Check access_token_response for id_token (alternative location)
    if (!idToken && response.access_token_response?.id_token) {
      idToken = response.access_token_response.id_token;
      console.log(`[${timestamp}] Found id_token in access_token_response`);
    }

    if (idToken) {
      localStorage.setItem(this.ID_TOKEN_KEY, idToken);
      console.log(`[${timestamp}] ✅ ID token stored`);
    } else {
      console.warn(`[${timestamp}] ⚠️ No ID token found in response!`);
      if (environment.oauth2Config?.logsEnabled) {
        console.log(`[${timestamp}] Full response:`, JSON.stringify(response, null, 2));
      }
    }

    if (environment.oauth2Config?.logsEnabled) {
      console.log(`[${timestamp}] Tokens stored successfully`);
    }
  }

  /**
   * Clears all stored tokens
   */
  private clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.ID_TOKEN_KEY);
    localStorage.removeItem(this.EXPIRES_AT_KEY);

    if (environment.oauth2Config?.logsEnabled) {
      console.log('Tokens cleared');
    }
  }
}
